/**
 * Created by spepa on 01.03.2024
 */

const UnitMagnetHandler = function () {
    cleverapps.EventEmitter.call(this);
    this.targetUnits = [];
    this.targetCell = undefined;
};

UnitMagnetHandler.prototype = Object.create(cleverapps.EventEmitter.prototype);
UnitMagnetHandler.prototype.constructor = UnitMagnetHandler;

UnitMagnetHandler.prototype.resetTargets = function () {
    if (this.sfxInterval) {
        cleverapps.timeouts.clearInterval(this.sfxInterval);
        delete this.sfxInterval;
    }

    if (this.sfxActive) {
        cleverapps.audio.stopSound(this.sfxActive);
        delete this.sfxActive;
    }

    this.clearHighlight();
    if (this.targetUnits.length > 0) {
        this.targetUnits[0].unhighlightMerge();
        this.targetUnits[0].unhighlight();
        this.targetUnits = [];
    }
};

UnitMagnetHandler.prototype.clearHint = function () {
    cleverapps.centerHint.hideHint();
    delete this.hintActive;
    delete this.hintTimeout;
};

UnitMagnetHandler.prototype.clearHighlight = function () {
    if (this.highlight) {
        this.highlight.remove();
        delete this.highlight;
    }
};

UnitMagnetHandler.prototype.highlightCell = function (cell) {
    this.clearHighlight();
    this.highlight = new Highlight(cell.x, cell.y);
    Map2d.currentMap.onAdd(Map2d.LAYER_UNITS, this.highlight.x, this.highlight.y, this.highlight);
};

UnitMagnetHandler.prototype.processNoTarget = function () {
    this.trigger("updateDragView", { animation: "idle" });
    this.resetTargets();
    if (this.hintTimeout) {
        this.clearHint();
    }
};

UnitMagnetHandler.prototype.processNewTarget = function (newTargets) {
    if (this.hintTimeout || this.hintActive) {
        this.clearHint();
    }

    this.sfxActive = cleverapps.effects.fire("merge.unitmagnet.ready");
    this.sfxInterval = cleverapps.timeouts.setInterval(() => {
        this.sfxActive = cleverapps.effects.fire("merge.unitmagnet.ready");
    }, 2000);

    this.targetUnits = newTargets;
    this.targetUnits[0].highlightMerge([this.targetUnits]);

    this.trigger("updateDragView", { animation: "active" });
};

UnitMagnetHandler.prototype.processInvalidTarget = function (targetUnit) {
    if (!this.hintTimeout) {
        this.hintTimeout = true;

        cleverapps.centerHint.schedule(1000, () => {
            delete this.hintTimeout;
            this.hintActive = true;
            cleverapps.centerHint.createTextHint(Messages.get("UnitMagnet.cantUse"));
        });
    }

    this.targetUnits = [targetUnit];
    targetUnit.highlight({ isErrorLight: true });
    this.trigger("updateDragView", { animation: "idle" });
};

UnitMagnetHandler.prototype.invalidTouch = function (touch) {
    const toolbarBg = ComponentUtils.findChildByName(cleverapps.toolbar.onGetView(), "bg");
    return toolbarBg.hasTouch(touch);
};

UnitMagnetHandler.prototype.searchTargets = function (touch) {
    if (this.invalidTouch(touch)) {
        this.processNoTarget();
        this.clearHighlight();
        return;
    }

    const map = Map2d.currentMap;
    const cell = map.getMapView().getCellByTouch(touch);

    if (cc.pSameAs(this.targetCell, cell) || !map.isValid(cell.x, cell.y)) {
        return;
    }
    this.targetCell = cell;

    const targetUnit = map.getUnitWithHead(cell.x, cell.y);
    if (!targetUnit) {
        this.processNoTarget();
        this.highlightCell(cell);
        return;
    }
    if (targetUnit === this.targetUnits[0]) {
        return;
    }
    this.resetTargets();

    const sameUnits = this.findPullInUnits(targetUnit);
    const newTargets = [targetUnit].concat(sameUnits);

    const currentNeighbors = map.bfs(targetUnit.x, targetUnit.y, map.compareEqual.bind(map, targetUnit));

    if (newTargets.length > 1 && currentNeighbors.length < newTargets.length) {
        this.processNewTarget(newTargets);
    } else {
        this.processInvalidTarget(targetUnit);
    }
};

UnitMagnetHandler.prototype.pullInUnits = function () {
    if (this.targetUnits.length < 2 || !cleverapps.user.spendSoft(cleverapps.EVENTS.SPENT.MAGNET, UnitMagnetHandler.PRICE)) {
        this.trigger("updateDragView", { animation: "idle" });
        this.resetTargets();
        this.clearHint();
        return;
    }

    const targetUnit = this.targetUnits[0];
    const pullUnits = this.targetUnits.slice(1);
    this.resetTargets();

    new ActionPlayer([
        function (f) {
            cleverapps.effects.fire("merge.unitmagnet.use");
            cleverapps.timeouts.setTimeout(f, 1300);
        },

        function (f) {
            this.performPull(targetUnit, pullUnits);
            cleverapps.timeouts.setTimeout(f, 300);
        }.bind(this)
    ]).play();

    return true;
};

UnitMagnetHandler.prototype.findPullInUnits = function (targetUnit) {
    const canAffect = function (target) {
        return target.isMovable() && !target.isBlocked() && !target.isGrounded() && !target.isMultiCell() && !target.isDragged() && !target.isLifted();
    };
    if (!canAffect(targetUnit)) {
        return [];
    }

    return Map2d.currentMap.listAvailableUnits(targetUnit).filter((unit) => targetUnit !== unit && canAffect(unit));
};

UnitMagnetHandler.prototype._canDisplaceUnit = function (unit) {
    return !unit.isMultiCell() && !unit.isMultiCellBody() && (unit.isMovable() || unit.findComponent(Collectible));
};

UnitMagnetHandler.prototype._getTargetCells = function (startCell) {
    const map = Map2d.currentMap;
    const mapSize = cc.size(map.getWidth(), map.getHeight());
    const hardTerrain = 1e3;
    const paths = {};
    const pathsToRevaluate = {};

    const getCellCost = function (x, y) {
        if (!map.isGround(x, y) || map.getFog(x, y)) {
            return hardTerrain;
        }
        const unit = map.getUnit(x, y);
        return !unit || this._canDisplaceUnit(unit) ? 1 : hardTerrain;
    }.bind(this);

    const evaluateCell = function (x, y) {
        if (startCell.x === x && startCell.y === y) {
            return 1;
        }
        const ownCost = getCellCost(x, y);
        let pathCost = 1e6;

        ISO_NEIGHBORS.forEach((dir) => {
            const neighbor = paths[x + dir.x + (y + dir.y) * mapSize.width];
            if (neighbor && (pathCost > ownCost + neighbor.cost)) {
                pathCost = ownCost + neighbor.cost;
            }
        });
        return pathCost;
    };

    map.bfs(startCell.x, startCell.y, (x, y) => {
        if (y >= mapSize.height || y < 0 || x >= mapSize.width || x < 0) {
            return false;
        }
        const key = x + y * mapSize.width;
        paths[key] = { cost: evaluateCell(x, y), x, y };
        if (paths[key].cost > hardTerrain) {
            pathsToRevaluate[key] = paths[key];
        }
        return true;
    });

    while (Object.keys(pathsToRevaluate).length > 0) {
        for (const key in pathsToRevaluate) {
            const path = pathsToRevaluate[key];
            const newCost = evaluateCell(path.x, path.y);
            delete pathsToRevaluate[key];

            if (newCost < path.cost) {
                paths[key].cost = newCost;
                ISO_NEIGHBORS.forEach((dir) => {
                    const neighborKey = path.x + dir.x + (path.y + dir.y) * mapSize.width;
                    if (paths[neighborKey] && paths[neighborKey].cost > newCost && paths[neighborKey].cost > hardTerrain) {
                        pathsToRevaluate[neighborKey] = paths[neighborKey];
                    }
                });
            }
        }
    }

    return Object.values(paths).sort((a, b) => a.cost - b.cost).map((item) => ({ x: item.x, y: item.y }));
};

UnitMagnetHandler.prototype.performPull = function (targetUnit, pullUnits) {
    const isBuilt = targetUnit.isBuilt();
    pullUnits = pullUnits.sort((a, b) => {
        if (isBuilt === a.isBuilt() && isBuilt !== b.isBuilt()) {
            return -1;
        }
        if (isBuilt !== a.isBuilt() && isBuilt === b.isBuilt()) {
            return 1;
        }
        return 0;
    });

    const map = Map2d.currentMap;
    const targetCells = this._getTargetCells(targetUnit);
    const unitsToDisplace = [];
    let pulled = 0;

    for (const i in targetCells) {
        if (pulled === pullUnits.length) {
            break;
        }
        const x = targetCells[i].x;
        const y = targetCells[i].y;

        if (!map.isGround(x, y) || map.getFog(x, y)) {
            continue;
        }

        const unit = map.getUnit(x, y);
        if (unit) {
            if (unit === targetUnit || !this._canDisplaceUnit(unit)) {
                continue;
            }

            const index = pullUnits.indexOf(unit);
            if (index !== -1) {
                pullUnits.splice(index, 1);
                continue;
            }
            unit.liftUnitFromMap();
            unitsToDisplace.push(unit);
        }

        const pullingUnit = pullUnits[pulled++];
        if (!pullingUnit.onGetView() && Map2d.currentMap.isVisibleCell(x, y)) {
            Map2d.currentMap.onAddUnit(pullingUnit.x, pullingUnit.y, pullingUnit);
        }
        pullingUnit.move(x, y);
    }

    unitsToDisplace.forEach((unit) => {
        const cell = Map2d.currentMap.findEmptySlot(targetUnit.x, targetUnit.y);
        unit.move(cell.x, cell.y);
        if (!unit.onGetView() && Map2d.currentMap.isVisibleCell(cell.x, cell.y)) {
            Map2d.currentMap.onAddUnit(unit.x, unit.y, unit);
        }
    });
};

UnitMagnetHandler.PRICE = 25;